import React, { useState } from 'react';
import { BaseConfigProps, commonStyles } from '../shared/ConfigFormHelpers';
import CollapsibleSection from '../shared/CollapsibleSection';

const Base64CoderConfig: React.FC<BaseConfigProps> = ({ node, updateConfig }) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['operation', 'input']));
  
  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(section)) {
        newSet.delete(section);
      } else {
        newSet.add(section);
      }
      return newSet;
    });
  };

  const operations = [
    { value: 'encode', label: 'Encode', icon: '🔒', desc: 'Szöveg Base64-re kódolása' },
    { value: 'decode', label: 'Decode', icon: '🔓', desc: 'Base64 visszafejtése szöveggé' },
  ];

  return (
    <div className="space-y-3">
      {/* Művelet típus */}
      <CollapsibleSection
        id="operation"
        title="Művelet Típusa"
        icon="🔄"
        color="lime"
        isExpanded={expandedSections.has('operation')}
        onToggle={toggleSection}
      >
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            {operations.map((op) => (
              <button
                key={op.value}
                onClick={() => updateConfig('operation', op.value)}
                className={`flex flex-col items-center p-4 rounded-md border text-sm transition-all ${
                  (node.data.config.operation || 'encode') === op.value
                    ? 'border-lime-500 bg-lime-50 dark:bg-lime-900/20 text-lime-700 dark:text-lime-300'
                    : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:border-gray-400'
                }`}
              >
                <span className="text-3xl mb-2">{op.icon}</span>
                <div className="font-medium">{op.label}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400 text-center mt-1">{op.desc}</div>
                {(node.data.config.operation || 'encode') === op.value && (
                  <span className="text-lime-500 mt-2">✓</span>
                )}
              </button>
            ))}
          </div>

          <div className="bg-lime-50 dark:bg-lime-900/20 border border-lime-200 dark:border-lime-800 rounded-md p-3">
            <div className="text-xs text-lime-700 dark:text-lime-300">
              <strong>💡 Base64:</strong> Bináris adatok szöveges formában történő tárolására és továbbítására használatos.
            </div>
          </div>
        </div>
      </CollapsibleSection>

      {/* Input */}
      <CollapsibleSection
        id="input"
        title="Bemenet"
        icon="📄"
        color="blue"
        isExpanded={expandedSections.has('input')}
        onToggle={toggleSection}
      >
        <div className="space-y-3">
          <div>
            <label className={commonStyles.label}>
              {node.data.config.operation === 'encode' ? 'Eredeti szöveg' : 'Base64 szöveg'}
            </label>
            <textarea
              value={node.data.config.input || ''}
              onChange={(e) => updateConfig('input', e.target.value)}
              className={`${commonStyles.textarea} font-mono text-xs`}
              rows={6}
              placeholder={node.data.config.operation === 'encode' 
                ? 'Hello World!\n\nvaggy {{text_data}}'
                : 'SGVsbG8gV29ybGQh\n\nvaggy {{base64_data}}'
              }
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {node.data.config.operation === 'encode' ? 'Kódolandó szöveg vagy változó' : 'Dekódolandó Base64 vagy változó'}
            </p>
          </div>

          {node.data.config.operation === 'encode' && (
            <div>
              <label className={commonStyles.label}>
                Karakterkódolás
              </label>
              <select
                value={node.data.config.encoding || 'utf-8'}
                onChange={(e) => updateConfig('encoding', e.target.value)}
                className={commonStyles.select}
              >
                <option value="utf-8">UTF-8</option>
                <option value="utf-16">UTF-16</option>
                <option value="ascii">ASCII</option>
                <option value="latin1">Latin-1</option>
              </select>
            </div>
          )}
        </div>
      </CollapsibleSection>

      {/* Kimenet Változók */}
      <CollapsibleSection
        id="variables"
        title="Kimenet Változók"
        icon="🔗"
        color="purple"
        isExpanded={expandedSections.has('variables')}
        onToggle={toggleSection}
      >
        <div className="space-y-2">
          <div className="text-xs text-gray-600 dark:text-gray-400 mb-3">
            A művelet után ezek a változók lesznek elérhetők:
          </div>
          
          <div className="grid grid-cols-1 gap-2">
            {[
              { var: '{{result}}', desc: node.data.config.operation === 'encode' ? 'Base64 szöveg' : 'Dekódolt szöveg', example: node.data.config.operation === 'encode' ? 'SGVsbG8=' : 'Hello World' },
              { var: '{{length}}', desc: 'Eredmény hossza', example: '16' },
              { var: '{{operation}}', desc: 'Művelet típusa', example: node.data.config.operation || 'encode' }
            ].map((item) => (
              <div key={item.var} className="bg-gray-50 dark:bg-gray-800 rounded-md p-2 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <code className="text-xs font-mono text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20 px-2 py-1 rounded">
                    {item.var}
                  </code>
                  <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                    {item.example}
                  </span>
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  {item.desc}
                </div>
              </div>
            ))}
          </div>
        </div>
      </CollapsibleSection>

      {/* Példák */}
      <CollapsibleSection
        id="examples"
        title="Használati Példák"
        icon="💡"
        color="green"
        isExpanded={expandedSections.has('examples')}
        onToggle={toggleSection}
      >
        <div className="space-y-2 text-xs text-gray-600 dark:text-gray-400">
          <div className="bg-gray-50 dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-700">
            <strong>🔒 API Authentication:</strong>
            <div className="mt-1 font-mono text-[10px]">
              Változó (username:password) → Base64 Encode → HTTP Request
            </div>
          </div>
          <div className="bg-gray-50 dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-700">
            <strong>📧 Email melléklet:</strong>
            <div className="mt-1 font-mono text-[10px]">
              Fájl olvasás → Base64 Encode → Email küldés
            </div>
          </div>
          <div className="bg-gray-50 dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-700">
            <strong>🔓 Token dekódolás:</strong>
            <div className="mt-1 font-mono text-[10px]">
              HTTP Request → Base64 Decode → JSON Processor
            </div>
          </div>
        </div>
      </CollapsibleSection>
    </div>
  );
};

export default Base64CoderConfig;
